type AnyFn = (...args: never[]) => unknown;

/** Keys of `T` whose values are callable. */
type MethodKeys<T> = {
	[K in keyof T]: T[K] extends AnyFn ? K : never;
}[keyof T];

const map = new WeakMap<object, Map<AnyFn, AnyFn>>();

function add(context: object, methods: Map<AnyFn, AnyFn>): Map<AnyFn, AnyFn> {
	map.set(context, methods);
	return methods;
}

function set<F extends AnyFn>(methods: Map<AnyFn, AnyFn>, method: AnyFn, bound: F): F {
	methods.set(method, bound);
	return bound;
}

/**
 * Binds `fn` to `context` and caches the result, so repeated calls with the
 * same `(context, method)` pair return the *same* bound function reference.
 *
 * Useful as a stable callback identity (e.g. for React props or event
 * listeners) without re-creating a new `.bind()` closure on every render/call.
 *
 * @param context The object to bind to (typically a class instance).
 * @param fn      A function to bind, or the name of a method on `context`.
 */
export function boundOnce<F extends AnyFn>(context: object, fn: F): F;
export function boundOnce<T extends object, K extends MethodKeys<T>>(context: T, fn: K): T[K];
export function boundOnce(context: object, fn: AnyFn | PropertyKey): AnyFn {
	const method: AnyFn =
		typeof fn === "function" ? fn : (context as Record<PropertyKey, AnyFn>)[fn];
	const methods = map.get(context) ?? add(context, new Map());
	return methods.get(method) ?? set(methods, method, method.bind(context) as AnyFn);
}
